import { useMemo } from 'react';
import { DEFAULT_SIZES, resolveColor, stickyColor, type ObjectType, type TemplateContent } from '@lumo/shared';
import { shapeGeometry } from '@lumo/engine/geometry';
import { useTheme } from '@/lib/theme';
import { cn } from '@/lib/cn';

/** Схематичное превью шаблона в SVG (без рендера движка). */
export function TemplatePreview({ content, className }: { content: TemplateContent; className?: string }) {
  const theme = useTheme((s) => s.resolved);
  const view = useMemo(() => {
    const objs = content.objects.map((o) => {
      const def = DEFAULT_SIZES[o.type as ObjectType];
      return { ...o, x: o.x ?? 0, y: o.y ?? 0, w: o.w ?? def.w, h: o.h ?? def.h };
    });
    if (!objs.length) return null;
    const minX = Math.min(...objs.map((o) => o.x));
    const minY = Math.min(...objs.map((o) => o.y));
    const maxX = Math.max(...objs.map((o) => o.x + o.w));
    const maxY = Math.max(...objs.map((o) => o.y + o.h));
    const pad = Math.max(maxX - minX, maxY - minY) * 0.06;
    return { objs, box: `${minX - pad} ${minY - pad} ${maxX - minX + pad * 2} ${maxY - minY + pad * 2}` };
  }, [content]);

  if (!view) {
    return <div className={cn(content.meta?.grid === 'lines' ? 'bg-[linear-gradient(var(--lumo-canvas-dot)_1px,transparent_1px),linear-gradient(90deg,var(--lumo-canvas-dot)_1px,transparent_1px)] bg-[size:16px_16px]' : 'canvas-dots', 'bg-canvas', className)} />;
  }
  const byId = new Map(view.objs.map((o) => [o.id, o]));
  const ink = theme === 'dark' ? '#d6d3d1' : '#44403c';
  return (
    <div className={cn('canvas-dots overflow-hidden', className)} aria-hidden>
      <svg viewBox={view.box} preserveAspectRatio="xMidYMid meet" className="size-full">
        {view.objs.map((o) => {
          const p = (o.props ?? {}) as Record<string, string>;
          switch (o.type) {
            case 'frame':
              return <rect key={o.id} x={o.x} y={o.y} width={o.w} height={o.h} rx={8} fill={resolveColor(p.background ?? 'fill:white', theme)} stroke={theme === 'dark' ? '#57534e' : '#d6d3d1'} strokeWidth={4} />;
            default:
              return null;
          }
        })}
        {view.objs.map((o) => {
          const p = (o.props ?? {}) as Record<string, string>;
          switch (o.type) {
            case 'sticky':
              return (
                <g key={o.id}>
                  <rect x={o.x} y={o.y} width={o.w} height={o.h} rx={4} fill={stickyColor(p.color ?? 'yellow', theme)} />
                  {!!o.text && <rect x={o.x + o.w * 0.18} y={o.y + o.h * 0.42} width={o.w * 0.64} height={o.h * 0.08} rx={4} fill="#1c1917" opacity={0.18} />}
                </g>
              );
            case 'shape': {
              const g = shapeGeometry((p.kind as never) ?? 'rect', o.w, o.h);
              return <path key={o.id} d={g.outline.map((pt, i) => `${i ? 'L' : 'M'}${o.x + pt.x} ${o.y + pt.y}`).join('') + 'Z'} fill={resolveColor(p.fill ?? 'fill:white', theme)} stroke={ink} strokeWidth={4} />;
            }
            case 'text':
              return <rect key={o.id} x={o.x} y={o.y + o.h * 0.25} width={Math.min(o.w, 520)} height={o.h * 0.5} rx={6} fill={ink} opacity={0.65} />;
            case 'card':
              return (
                <g key={o.id}>
                  <rect x={o.x} y={o.y} width={o.w} height={o.h} rx={12} fill={theme === 'dark' ? '#2a2725' : '#ffffff'} stroke={theme === 'dark' ? '#57534e' : '#e7e3dd'} strokeWidth={3} />
                  <rect x={o.x} y={o.y} width={8} height={o.h} rx={4} fill="#6E56CF" />
                  <rect x={o.x + 24} y={o.y + 24} width={o.w * 0.6} height={16} rx={8} fill={ink} opacity={0.5} />
                </g>
              );
            case 'mindmap-node':
              return <rect key={o.id} x={o.x} y={o.y} width={o.w} height={o.h} rx={o.h / 2} fill={p.parentNodeId ? (theme === 'dark' ? '#2a2725' : '#fff') : resolveColor(p.branchColor ?? 'ink:violet', theme)} stroke={resolveColor(p.branchColor ?? 'ink:violet', theme)} strokeWidth={4} />;
            case 'connector': {
              const props = o.props as { from?: { kind: string; objectId?: string }; to?: { kind: string; objectId?: string } };
              const a = props.from?.objectId ? byId.get(props.from.objectId) : null;
              const b = props.to?.objectId ? byId.get(props.to.objectId) : null;
              if (!a || !b) return null;
              return <line key={o.id} x1={a.x + a.w / 2} y1={a.y + a.h / 2} x2={b.x + b.w / 2} y2={b.y + b.h / 2} stroke={ink} strokeWidth={4} />;
            }
            default:
              return null;
          }
        })}
      </svg>
    </div>
  );
}
